import { saveAs } from "file-saver";
import JSZip from "jszip";

import { useEffect, useMemo, useRef } from "react";
import { downloadArtifact } from "./v2/requests";
import { type DocumentSummary, type DocumentSummaryRelease } from "./types";
import { DocumentArtifactStatus } from "~/config/types";
import i18n from "~/i18n";
import { getNoTierLabel } from "~/helpers/tier";

export interface DownloadableRelease {
  document: DocumentSummary;
  release: DocumentSummaryRelease;
  status: DocumentArtifactStatus;
}

const statusOrder = [
  DocumentArtifactStatus.published,
  DocumentArtifactStatus.draft,
  DocumentArtifactStatus.archived,
];

const sanitizeZipName = (name: string) =>
  name.replace(/[\\/:*?"<>|]/g, "-").trim() || "_";

export const Utils = {
  getReleaseStatuses: (
    downloads: DownloadableRelease[],
  ): DocumentArtifactStatus[] =>
    statusOrder.filter((status) =>
      downloads.some((download) => download.status === status),
    ),

  getDownloadableReleases: (
    documents: DocumentSummary[],
  ): DownloadableRelease[] =>
    documents.flatMap((document) => {
      const downloads: DownloadableRelease[] = [];
      if (document.latestPublishedRelease) {
        downloads.push({
          document,
          release: document.latestPublishedRelease,
          status: DocumentArtifactStatus.published,
        });
      }
      if (
        document.latestRelease.artifact.status !==
        DocumentArtifactStatus.published
      ) {
        downloads.push({
          document,
          release: document.latestRelease,
          status: document.latestRelease.artifact.status,
        });
      }
      return downloads;
    }),

  saveZipDocuments: async (
    downloads: DownloadableRelease[],
    updateProgress: (value: number) => void,
  ) => {
    const zip = new JSZip();
    const statusLabel: Record<DocumentArtifactStatus, string> = {
      [DocumentArtifactStatus.draft]: i18n.t(
        ($) => $.commons.document.statuses.draft,
      ),
      [DocumentArtifactStatus.published]: i18n.t(
        ($) => $.commons.document.statuses.published,
      ),
      [DocumentArtifactStatus.archived]: i18n.t(
        ($) => $.commons.document.statuses.archived,
      ),
    };
    const splitByStatus = Utils.getReleaseStatuses(downloads).length > 1;
    let count = 0;

    for (const { document: doc, release, status } of downloads) {
      const artifact = release.artifact;
      const blob = await downloadArtifact(artifact.id);

      let folder = zip
        .folder(sanitizeZipName(doc.category.title))
        ?.folder(sanitizeZipName(doc.tier?.title ?? getNoTierLabel(i18n.t)));
      if (splitByStatus) {
        folder = folder
          ?.folder(sanitizeZipName(doc.name))
          ?.folder(statusLabel[status]);
      }
      folder?.file(
        `${sanitizeZipName(artifact.title)}_${artifact.id}.${artifact.fileType}`,
        blob,
      );
      count++;
      updateProgress(count);
    }
    await zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "documents");
    });
  },
};

/**
 * Debounce a callback function
 */
export function useDebounce<
  Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
  const callbackRef = useRef(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Create a stable debounced function wrapper
  return useMemo(() => {
    return (...args: Parameters<Callback>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Access ref in the timeout callback, not during render
        callbackRef.current(...args);
      }, delay);
    };
  }, [delay]);
}
